import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import MerchantLayout from '@/components/layouts/MerchantLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Inbox, Plus, Clock, CheckCircle, XCircle,
  Loader2, Search, Building2, Eye, Sparkles
} from 'lucide-react';
import { getApiUrl } from '@/lib/api';

const STATUS_BADGE: Record<string, { label: string; color: string }> = {
  VALUABLE: { label: 'Valuable', color: 'bg-emerald-500/10 text-emerald-400' },
  DELIVERED: { label: 'Delivered', color: 'bg-blue-500/10 text-blue-400' },
  ACCEPTED: { label: 'Accepted', color: 'bg-emerald-500/10 text-emerald-400' },
  REJECTED: { label: 'Rejected', color: 'bg-rose-500/10 text-rose-400' },
};

export default function MerchantPitchesPage() {
  const { token } = useAuth();
  const [pitches, setPitches] = useState<unknown[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => { fetchPitches(); }, []);

  const fetchPitches = async () => {
    try {
      const res = await fetch(getApiUrl('/api/v2/merchant/engagement/pitches?limit=100'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setPitches(await res.json());
    } catch (err) {
      console.error('Failed to load pitches:', err);
    } finally {
      setLoading(false);
    }
  };

  const pitchList = pitches as Array<{ id: string; product_name?: string; vendor_name?: string; category?: string; status_v2?: string; effective_score?: number }>;
  const filteredPitches = pitchList.filter(p => {
    const matchesSearch = p.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.vendor_name && p.vendor_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (p.category && p.category.toLowerCase().includes(searchTerm.toLowerCase()));
    if (filterStatus === 'all') return matchesSearch;
    return matchesSearch && p.status_v2 === filterStatus;
  });

  const filterOptions = [
    { value: 'all', label: 'All' },
    { value: 'VALUABLE', label: 'Valuable' },
    { value: 'DELIVERED', label: 'Delivered' },
    { value: 'ACCEPTED', label: 'Accepted' },
    { value: 'REJECTED', label: 'Rejected' },
  ];

  return (
    <MerchantLayout>
      <div className="space-y-6" data-testid="merchant-pitches-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">All Pitches</h1>
            <p className="text-zinc-500 mt-1">Browse all evaluated vendor pitches</p>
          </div>
          <Link to="/merchant/pitches/add">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-pitch-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Manually
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              placeholder="Search by product, vendor, or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11 bg-[#18181b] border-white/10 text-white placeholder-zinc-500"
              data-testid="search-pitches"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {filterOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setFilterStatus(opt.value)}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  filterStatus === opt.value
                    ? 'bg-rose-500 text-white'
                    : 'bg-[#18181b] border border-white/5 text-zinc-400 hover:bg-white/5'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Pitches List */}
        <div className="bg-[#18181b] rounded-xl border border-white/5 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-zinc-600 mx-auto" />
            </div>
          ) : filteredPitches.length === 0 ? (
            <div className="p-12 text-center">
              <Inbox className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400 font-medium">No pitches found</p>
              <p className="text-zinc-600 text-sm mt-1">Evaluated pitches will appear here</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {filteredPitches.map((pitch) => {
                const badge = STATUS_BADGE[pitch.status_v2 ?? ''] || STATUS_BADGE.VALUABLE;
                return (
                  <Link
                    key={pitch.id}
                    to={`/merchant/pitches/${pitch.id}`}
                    className="flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                    data-testid={`pitch-item-${pitch.id}`}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="p-2 rounded-lg bg-emerald-500/10">
                        <Building2 className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-white truncate">{pitch.product_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {pitch.vendor_name && <span className="text-sm text-zinc-500">{pitch.vendor_name}</span>}
                          {pitch.category && (
                            <>
                              <span className="text-xs text-zinc-600">·</span>
                              <span className="text-xs text-zinc-600">{pitch.category}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {pitch.effective_score != null && (
                        <div className="text-right hidden sm:block">
                          <p className={`text-sm font-semibold ${pitch.effective_score >= 70 ? 'text-emerald-400' : pitch.effective_score >= 50 ? 'text-blue-400' : 'text-zinc-400'}`}>
                            {Math.round(pitch.effective_score)}
                          </p>
                        </div>
                      )}
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                        {badge.label}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </MerchantLayout>
  );
}
